import React, { useState, useEffect, useContext, ChangeEvent } from 'react';
import { Redirect } from 'react-router-dom';
import CasesAPI from 'api/CasesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { useSnackbar } from 'notistack';
import ContentError from 'components/Common/ContentError';
import CaseCard from 'components/CaseManagement/CaseCard';
import CaseTable from 'components/CaseManagement/CaseTable';
import CaseFilter from 'components/CaseManagement/CaseFilter';
import { Grid, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { buildCaseQuery } from 'utils/queryHelper';
import {
  CustomSwitch,
  VaultCustomLabel,
} from 'components/Common/MUIStyledComponents';
import VaultHeaderLayout from 'components/CaseManagement/HeaderLayout';
import SubscribeModal from 'components/SubscribeModal';
import { Player } from '@lottiefiles/react-lottie-player';

const resultsPerPage = Number(process.env.REACT_APP_RESULTS_PER_PAGE);

interface ICaseFilterOptions {
  searchVal: string;
  docType: string;
  endDt: string;
  startDt: string;
}

const VaultHome: React.FC = (props: any) => {
  const { cur_user } = useContext(GlobalContext);
  const tabState = props.location.state;
  const [ cases, setCases ] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });
  const [ myCases, setMyCases ] = useState<{
    isLoaded: boolean;
    data: any[];
  }>({ isLoaded: false, data: [] });
  const [ sharedCases, setSharedCases ] = useState<{
    isLoaded: boolean;
    data: any[];
  }>({ isLoaded: false, data: [] });
  const [ caseView, setCaseView ] = useState(true);
  const [ caseQuery, setCaseQuery ] = useState<any>({});
  const [ caseCount, setCaseCount ] = useState<number>(0);
  const [ pageCount, setPageCount ] = useState<number>(0);
  const [ pageNum, setPageNum ] = useState<number>(1);
  const { enqueueSnackbar } = useSnackbar();
  const [ activeTab, setActiveTab ] = useState('all');
  const [ userHasVault, setUserHasVault ] = useState(false);
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');
  const [ activeSub, setActiveSub ] = useState(false);

  const checkForVault = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('standard') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasVault(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Standard' ||
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    checkForVault();
  }, [ activeSub ]);

  const handleTabChange = (tab: string) => {
    switch (tab) {
    case 'all':
      setActiveTab('all');
      window.sessionStorage.setItem('activeTab', 'all');
      break;
    case 'myVault':
      setActiveTab('myVault');
      window.sessionStorage.setItem('activeTab', 'myVault');
      break;
    case 'shared':
      setActiveTab('shared');
      window.sessionStorage.setItem('activeTab', 'shared');
      break;
    default:
      setActiveTab('all');
    }
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const getCasesFromAPI = async(query: any, page: number) => {
    const mongoReq = {
      query: {
        ...query,
        groupId: cur_user.selected_team?.id,
        $or: [
          { sharedWith: cur_user.basics?.id },
          { createdBy: cur_user.basics?.id },
        ],
      },
      skip: (page - 1) * resultsPerPage,
      limit: resultsPerPage,
    };
    getCaseCountFromAPI(mongoReq);
    const resp = await CasesAPI.searchCases(mongoReq);
    if (resp?.success) {
      setCases({ data: resp?.result, isLoaded: true, error: undefined });
    } else if (resp) {
      const error_msg = `Error querying cases: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setCases({ data: [], isLoaded: true, error: error_msg });
    }
  };

  const getCaseCountFromAPI = async(query: any) => {
    const resp = await CasesAPI.countCases(query);
    if (resp?.success) {
      const totalCount = resp?.result;
      setCaseCount(totalCount);
      setPageCount(Math.ceil(totalCount / resultsPerPage));
    } else if (resp) {
      const error_msg = `Error getting total number of case results: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const handleAdvancedSearch = async(filterOptions: ICaseFilterOptions) => {
    const new_query = buildCaseQuery(
      filterOptions.searchVal,
      filterOptions.docType,
      filterOptions.startDt,
      filterOptions.endDt,
    );
    setCaseQuery(new_query);
    getCasesFromAPI(new_query, pageNum);
  };

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    setPageNum(new_page);
    getCasesFromAPI(caseQuery, new_page);
  };

  const getMyCases = () => {
    const my: any = [];
    cases.data.map((c: any) => {
      if (c.createdBy === cur_user?.basics?.id) {
        my.push(c);
      }
    });
    setMyCases({ isLoaded: true, data: my });
  };

  const getSharedCases = () => {
    const shared: any = [];
    cases.data.map((c: any) => {
      if (c.sharedWith.includes(cur_user?.basics?.id)) {
        shared.push(c);
      }
    });
    setSharedCases({ isLoaded: true, data: shared });
  };

  const allCaseCards = cases.data.map((c: any, i) => (
    <CaseCard key={i} caseData={c} />
  ));

  const myCaseCards = myCases.data.map((c: any, i) => (
    <CaseCard key={i} caseData={c} />
  ));

  const sharedCaseCards = sharedCases.data.map((c: any, i) => (
    <CaseCard key={i} caseData={c} />
  ));

  useEffect(() => {
    const storedCaseViewState = window.localStorage.getItem('caseViewState');
    setCaseView(storedCaseViewState !== 'false');

    if (tabState) {
      handleTabChange(tabState.tab);
    }
  }, []);

  useEffect(() => {
    const caseViewString = JSON.stringify(caseView);
    window.localStorage.setItem('caseViewState', caseViewString);
  }, [ caseView ]);

  useEffect(() => {
    getCasesFromAPI({}, 1);
  }, [ cur_user.selected_team ]);

  useEffect(() => {
    getMyCases();
    getSharedCases();
  }, [ cases.data ]);

  if (!cases.isLoaded) {
    return (
      <div className='spinner-spacer-table'>
        <div className='loader'>Loading...</div>
      </div>
    );
  }
  if (cases.error) {
    return <ContentError error={cases.error} />;
  }

  return (
    <VaultHeaderLayout title='Analytics Vault'>
      {activeSub && !userHasVault ? (
        <div className='no-license-modal'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          ></Player>
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1',
            }}
          >
            Visit Account &gt; Users &gt; to assign an available license to your
            user or contact your team admin
          </Typography>
        </div>
      ) : !userHasVault ? (
        <SubscribeModal
          name='Analytics Vault'
          description='Keep track of all risk reports, investigative analysis and trend reports'
          price={25}
          curUser={cur_user}
          stripeTeam={matchedTeamWithStripe}
          activeSub={activeSub}
        />
      ) : (
        <></>
      )}
      <div className={`os-iframe${!userHasVault ? '-blur' : ''}`}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className='case-btns-container'>
              <div
                className={`case-btn-${activeTab === 'all' ? 'selected' : 'default'
                }`}
                onClick={() => handleTabChange('all')}
              >
                <div className='case-btn-text'>All</div>
              </div>
              <div
                className={`case-btn-${activeTab === 'myVault' ? 'selected' : 'default'
                }`}
                onClick={() => handleTabChange('myVault')}
              >
                <div className='case-btn-text'>My Cases</div>
              </div>
              <div
                className={`case-btn-${activeTab === 'shared' ? 'selected' : 'default'
                }`}
                onClick={() => handleTabChange('shared')}
              >
                <div className='case-btn-text'>Shared With Me</div>
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <CaseFilter handleSubmit={handleAdvancedSearch} />
          </Grid>

          <Grid item xs={12}>
            <div className='vault-results-nav'>
              {activeTab === 'all' && cases.isLoaded && (
                <p className='vault-results-header'>
                  {cases.data.length} tickets
                </p>
              )}
              {activeTab === 'myVault' && myCases.isLoaded && (
                <p className='vault-results-header'>
                  {myCases.data.length} tickets
                </p>
              )}
              {activeTab === 'shared' && sharedCases.isLoaded && (
                <p className='vault-results-header'>
                  {sharedCases.data.length} tickets
                </p>
              )}
              <VaultCustomLabel
                control={
                  <CustomSwitch
                    checked={caseView}
                    onChange={() => setCaseView(!caseView)}
                    name='checkedA'
                  />
                }
                label={caseView ? 'Cards' : 'List'}
              />
            </div>
          </Grid>
          <Grid
            item
            xs={12}
            container
            spacing={2}
            style={{ margin: 0, padding: 0 }}
          >
            {caseView && activeTab === 'all' && cases.isLoaded && allCaseCards}
            {caseView &&
              activeTab === 'myVault' &&
              myCases.isLoaded &&
              myCaseCards}
            {caseView &&
              activeTab === 'shared' &&
              sharedCases.isLoaded &&
              sharedCaseCards}
            {!caseView && activeTab === 'all' && (
              <Grid item xs={12}>
                <CaseTable cases={cases.data} borderRadius={8} />
              </Grid>
            )}
            {!caseView && activeTab === 'myVault' && (
              <Grid item xs={12}>
                <CaseTable cases={myCases.data} borderRadius={8} />
              </Grid>
            )}
            {!caseView && activeTab === 'shared' && (
              <Grid item xs={12}>
                <CaseTable cases={sharedCases.data} borderRadius={8} />
              </Grid>
            )}
          </Grid>
          <div className='pagination-block text-white'>
            <Pagination
              count={pageCount}
              color={undefined}
              onChange={handlePageChange}
            />
          </div>
        </Grid>
      </div>
    </VaultHeaderLayout>
  );
};
export default VaultHome;
